import { ApolloClient } from '@apollo/client'
import { bind, SUSPENSE } from '@react-rxjs/core'
import { catchError, combineLatest, map, Observable, of, switchMap } from 'rxjs'

import {
  ConfigurableProduct,
  GetProductsByUrlKeyDocument,
  Maybe,
  Money,
  SimpleProduct,
} from '../../graphql/magento'
import { coerceCaughtToLeft } from '../../utils/rx/errors'
import { fromObservableQuery } from '../../utils/rx/observable-query'
import { latestAccessToken$, latestAuthState$ } from '../auth/state'
import { latestApolloClient$ } from '../graphql/apollo'

const getProductsByUrlKeyObservableQueryFactory = ({
  client,
  token,
  urlKey,
}: {
  client: ApolloClient<unknown>
  token: string | undefined
  urlKey: string
}) =>
  client.watchQuery({
    query: GetProductsByUrlKeyDocument,
    context: { token },
    // override global error policy
    errorPolicy: 'none',
    variables: { urlKey },
  })

const watchGetProductsByUrlKeyFactory = (props: {
  client: ApolloClient<unknown>
  token: string | undefined
  urlKey: string
}) =>
  fromObservableQuery(getProductsByUrlKeyObservableQueryFactory(props)).pipe(
    // ignore `errors` in FetchResult since `errorPolicy` is `none` (Apollo will throw if errors returned from server)
    map(({ data }) => ({ _tag: 'Right' as const, data })),
    catchError(coerceCaughtToLeft),
  )

/**
 * Selection of properties from `GetProductsByUrlKeyQuery` that vary by authentication state.
 *
 * This can be removed if the distinction is unhelpful.
 */
export type GetProductsByUrlKeyQueryVaryByAuth = {
  __typename?: 'Query'
  products?: Maybe<{
    __typename?: 'Products'
    items?: Maybe<
      Array<
        Maybe<
          | {
              __typename?:
                | 'BundleProduct'
                | 'DownloadableProduct'
                | 'GroupedProduct'
                | 'SimpleProduct'
                | 'StoreCreditProduct'
                | 'VirtualProduct'
            }
          | ({
              __typename?: 'ConfigurableProduct'
            } & Pick<ConfigurableProduct, 'id' | 'sku'> & {
                variants?: Maybe<
                  Array<
                    Maybe<{
                      __typename?: 'ConfigurableVariant'
                      product?: Maybe<
                        {
                          __typename?: 'SimpleProduct'
                        } & Pick<SimpleProduct, 'id' | 'sku'> & {
                            price_tiers?: Maybe<
                              Array<
                                Maybe<{
                                  __typename?: 'TierPrice'
                                  final_price?: Maybe<
                                    { __typename?: 'Money' } & Pick<Money, 'currency' | 'value'>
                                  >
                                }>
                              >
                            >
                          }
                      >
                    }>
                  >
                >
              })
        >
      >
    >
  }>
}

/**
 * Monitor auth state and access token while querying product data that varies
 * by user (e.g. price). Return `SUSPENSE` until Auth0 has decided user is not
 * authenticated or an access token is available for the authenticated user.
 * Afterwards, return the observed product query.
 */
const productByUrlKeyForAuthFactory = (urlKey: string) =>
  combineLatest([latestAuthState$, latestAccessToken$, latestApolloClient$]).pipe(
    switchMap(
      ([authState, token, client]): Observable<
        | SUSPENSE
        | { _tag: 'Left'; error: Error }
        | { _tag: 'Right'; data: GetProductsByUrlKeyQueryVaryByAuth }
      > =>
        authState.isLoading || !urlKey || (authState.isAuthenticated && !token)
          ? of(SUSPENSE)
          : watchGetProductsByUrlKeyFactory({ client, token, urlKey }),
    ),
  )

export const [useQueryProductByUrlKeyVaryByAuth] = bind(productByUrlKeyForAuthFactory)

/**
 * Find variant by id in `GetProductsByUrlKeyQueryVaryByAuth` result. Useful for
 * extracting a corresponding variant in components relying on data that varies
 * by authentication state (e.g. price).
 */
export const findVariantInProductQuery = <T extends GetProductsByUrlKeyQueryVaryByAuth>(
  data: T,
  { itemId, sku }: { itemId: number; sku: string },
):
  | NonNullable<
      Extract<
        NonNullable<NonNullable<T['products']>['items']>[number],
        { __typename?: 'ConfigurableProduct' }
      >['variants']
    >[number]
  | undefined =>
  data.products?.items
    ?.find(
      (i): i is Extract<typeof i, { __typename?: 'ConfigurableProduct' }> =>
        i != null && i.__typename === 'ConfigurableProduct' && i.id === itemId,
    )
    ?.variants?.find(
      (v): v is typeof v & { product: NonNullable<NonNullable<typeof v>['product']> } =>
        v != null && v.product != null && v.product.sku === sku,
    )
